import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Search, Users, FolderOpen, Eye, FileText, Clock, CheckCircle, XCircle, AlertCircle, User, Briefcase, CreditCard, MapPin, Phone, Mail, Calendar, Building } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Define all Emp_Tab field groups for comprehensive display
const EMP_TAB_FIELD_GROUPS = {
  "Basic Information": [
    { key: "employee_id", label: "Employee ID" },
    { key: "gender", label: "Gender" },
    { key: "dob", label: "Date of Birth" },
    { key: "marital_status", label: "Marital Status" },
    { key: "nationality", label: "Nationality" },
  ],
  "Contact Information": [
    { key: "contact_number", label: "Contact Number" },
    { key: "alternate_number", label: "Alternate Number" },
    { key: "alternate_email", label: "Alternate Email" },
    { key: "present_address", label: "Present Address" },
    { key: "permanent_address", label: "Permanent Address" },
  ],
  "Employment Details": [
    { key: "employee_type", label: "Employee Type" },
    { key: "level", label: "Level" },
    { key: "department", label: "Department" },
    { key: "designation", label: "Designation" },
    { key: "project_name", label: "Project Name" },
    { key: "reporting_manager", label: "Reporting Manager" },
    { key: "rm_email", label: "RM Email" },
    { key: "hr_partner", label: "HR Partner" },
    { key: "hr_email", label: "HR Email" },
  ],
  "Dates & Experience": [
    { key: "actual_date_of_joining", label: "Actual Date of Joining" },
    { key: "converted_employee", label: "Converted Employee" },
    { key: "conversion_date", label: "Conversion Date" },
    { key: "total_work_experience", label: "Total Work Experience" },
    { key: "work_experience_as_employee", label: "Work Experience (Company)" },
    { key: "last_promotion", label: "Last Promotion" },
    { key: "active_date", label: "Active Date" },
    { key: "inactive_date", label: "Inactive Date" },
  ],
  "Education & Documents": [
    { key: "education", label: "Education" },
    { key: "previous_employment", label: "Previous Employment" },
    { key: "pan_number", label: "PAN Number" },
    { key: "aadhar_number", label: "Aadhar Number" },
    { key: "bank_details", label: "Bank Details" },
  ],
  "Assets": [
    { key: "company_asset", label: "Company Asset" },
    { key: "serial_number", label: "Serial Number" },
    { key: "payslip_folder_id", label: "Payslip Folder ID" },
  ],
  "Salary Structure": [
    { key: "salary_currency", label: "Salary Currency" },
    { key: "fixed_monthly_salary", label: "Fixed Monthly Salary" },
    { key: "internet_phone_allowance", label: "Internet/Phone Allowance" },
    { key: "monthly_performance_bonus", label: "Monthly Performance Bonus" },
    { key: "byod_allowance", label: "BYOD Allowance" },
    { key: "half_yearly_retention_bonus", label: "Half-Yearly Retention Bonus" },
    { key: "managerial_allowance", label: "Managerial Allowance" },
    { key: "provident_fund", label: "Provident Fund" },
    { key: "medical_allowance", label: "Medical Allowance" },
    { key: "gratuity", label: "Gratuity" },
    { key: "hra", label: "HRA" },
    { key: "ctc_yearly", label: "CTC (Yearly)" },
  ],
};

const HRPortal = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [portals, setPortals] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPortal, setSelectedPortal] = useState('');
  const [entries, setEntries] = useState([]);
  const [displayFields, setDisplayFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('emp-tab');
  
  // Employee details state
  const [empTabData, setEmpTabData] = useState([]);
  const [loadingEmpTab, setLoadingEmpTab] = useState(false);
  const [empTabSearchTerm, setEmpTabSearchTerm] = useState('');
  
  // Selected employee info
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState(null);
  const [employeeSummary, setEmployeeSummary] = useState(null);
  
  // Employee detail view
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);

  useEffect(() => {
    fetchPortals();
    fetchEmployees();
  }, []);

  const fetchPortals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-portal/portals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortals(response.data);
    } catch (error) {
      toast.error('Failed to load portals');
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-portal/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const fetchEmpTabData = useCallback(async () => {
    setLoadingEmpTab(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-portal/emp-tab`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmpTabData(response.data.employees || []);
    } catch (error) {
      toast.error('Failed to load employee data');
    } finally {
      setLoadingEmpTab(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'emp-tab') {
      fetchEmpTabData();
    }
  }, [activeTab, fetchEmpTabData]);

  const fetchEntries = async () => {
    if (!selectedEmployee || !selectedPortal) {
      toast.error('Please select both Employee and Portal');
      return;
    }

    setLoadingEntries(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/hr-portal/entries/${selectedEmployee}/${selectedPortal}?include_history=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(response.data.entries || []);
      setDisplayFields(response.data.display_fields || []);
      setSelectedEmployeeInfo(response.data.employee);
    } catch (error) {
      toast.error('Failed to load entries');
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  const fetchEmployeeSummary = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-portal/summary/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  };

  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeSummary(selectedEmployee);
    }
  }, [selectedEmployee]);

  const viewEmployeeDetail = (emp) => {
    setViewingEmployee(emp);
    setShowEmployeeDetail(true);
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEmpTab = empTabData.filter(emp =>
    emp.name?.toLowerCase().includes(empTabSearchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(empTabSearchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(empTabSearchTerm.toLowerCase()) ||
    emp.department?.toLowerCase().includes(empTabSearchTerm.toLowerCase())
  );

  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === '') return '-';
    
    // Format dates
    if (field.includes('date') || field.includes('_at') || field === 'dob') {
      try {
        return new Date(value).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return value;
      }
    }
    
    // Format boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Format status badges
    if (field === 'status' || field === 'approval_status') {
      const colors = {
        approved: 'bg-green-500',
        pending: 'bg-yellow-500',
        rejected: 'bg-red-500',
        completed: 'bg-green-500',
        in_progress: 'bg-blue-500',
        failed: 'bg-red-500'
      };
      return (
        <Badge className={colors[value?.toLowerCase()] || 'bg-gray-500'}>
          {value}
        </Badge>
      );
    }
    
    // Format passed field
    if (field === 'passed') {
      return value ? (
        <Badge className="bg-green-500">Passed</Badge>
      ) : (
        <Badge className="bg-red-500">Failed</Badge>
      );
    }
    
    // Format score
    if (field === 'score' || field === 'total_percentage') {
      return `${parseFloat(value).toFixed(1)}%`;
    }
    
    // Format salary/currency values
    if (field.includes('salary') || field.includes('allowance') || field.includes('bonus') || 
        field === 'ctc_yearly' || field === 'provident_fund' || field === 'gratuity' || 
        field === 'hra' || field === 'medical_allowance') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `₹${num.toLocaleString('en-IN')}`;
      }
    }
    
    return String(value);
  };

  const getFieldValue = (emp, key) => {
    // Check both user and emp_details
    let value = emp[key];
    if ((value === null || value === undefined || value === '') && emp.emp_details) {
      value = emp.emp_details[key];
    }
    return value;
  };

  const getGroupIcon = (groupName) => {
    const icons = {
      "Basic Information": User,
      "Contact Information": Phone,
      "Employment Details": Briefcase,
      "Dates & Experience": Calendar,
      "Education & Documents": FileText,
      "Assets": Building,
      "Salary Structure": CreditCard,
    };
    return icons[groupName] || FileText;
  };

  return (
    <div className="space-y-6" data-testid="hr-portal">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye className="w-6 h-6 text-teal-600" />
            <div>
              <CardTitle>HR Portal (Read-Only Access)</CardTitle>
              <CardDescription>
                View all employee data and portal history across all systems
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2" data-testid="hr-portal-tabs">
          <TabsTrigger value="emp-tab" data-testid="tab-emp-tab">
            <Users className="w-4 h-4 mr-2" />
            Employee Details (Emp_Tab)
          </TabsTrigger>
          <TabsTrigger value="view" data-testid="tab-view-portals">
            <FolderOpen className="w-4 h-4 mr-2" />
            View Portal Data
          </TabsTrigger>
        </TabsList>

        {/* Emp_Tab Tab - All Fields View */}
        <TabsContent value="emp-tab" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-lg">Employee Details (All Fields)</CardTitle>
                  <CardDescription>
                    Complete read-only view of all Emp_Tab information • Click on any employee to view full details
                  </CardDescription>
                </div>
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={empTabSearchTerm}
                    onChange={(e) => setEmpTabSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="emp-tab-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEmpTab ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="emp-tab-table">
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-bold">Employee ID</TableHead>
                        <TableHead className="font-bold">Name</TableHead>
                        <TableHead className="font-bold">Email</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Designation</TableHead>
                        <TableHead>Level</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEmpTab.map((emp) => (
                        <TableRow 
                          key={emp.id} 
                          className={`hover:bg-gray-50 ${emp.is_active === false ? 'bg-gray-100 opacity-70' : ''}`}
                          data-testid={`emp-row-${emp.employee_id}`}
                        >
                          <TableCell className="font-medium">{emp.employee_id}</TableCell>
                          <TableCell className="font-medium">{emp.name}</TableCell>
                          <TableCell className="text-sm text-gray-600">{emp.email}</TableCell>
                          <TableCell>{getFieldValue(emp, 'department') || '-'}</TableCell>
                          <TableCell>{getFieldValue(emp, 'designation') || '-'}</TableCell>
                          <TableCell>{getFieldValue(emp, 'level') || '-'}</TableCell>
                          <TableCell>{getFieldValue(emp, 'employee_type') || '-'}</TableCell>
                          <TableCell className="text-sm">{getFieldValue(emp, 'contact_number') || '-'}</TableCell>
                          <TableCell>
                            {emp.is_active !== false ? (
                              <Badge className="bg-green-500">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => viewEmployeeDetail(emp)}
                              data-testid={`view-detail-${emp.employee_id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View All
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="mt-4 text-sm text-gray-500">
                Showing {filteredEmpTab.length} of {empTabData.length} employees
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* View Portal Data Tab */}
        <TabsContent value="view" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Select Employee & Portal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Employee Search & Select */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search Employee</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, ID, or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="employee-search"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Employee</label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger data-testid="select-employee">
                      <SelectValue placeholder="Choose an employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredEmployees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          <div className="flex items-center gap-2">
                            <span className={emp.is_active === false ? 'text-gray-400' : ''}>
                              {emp.name} ({emp.employee_id})
                            </span>
                            {emp.is_active === false && (
                              <Badge variant="outline" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Portal</label>
                  <Select value={selectedPortal} onValueChange={setSelectedPortal}>
                    <SelectTrigger data-testid="select-portal">
                      <SelectValue placeholder="Choose a portal" />
                    </SelectTrigger>
                    <SelectContent>
                      {portals.map((portal) => (
                        <SelectItem key={portal.key} value={portal.key}>
                          {portal.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={fetchEntries} 
                disabled={!selectedEmployee || !selectedPortal || loadingEntries}
                className="w-full md:w-auto"
                data-testid="btn-view-data"
              >
                {loadingEntries ? 'Loading...' : 'View Data'}
              </Button>
            </CardContent>
          </Card>

          {/* Employee Summary Card */}
          {employeeSummary && (
            <Card className="bg-gradient-to-r from-teal-50 to-cyan-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">
                  {employeeSummary.employee.name} ({employeeSummary.employee.employee_id})
                </CardTitle>
                <CardDescription>
                  {employeeSummary.employee.department || 'No Department'} • {employeeSummary.employee.designation || 'No Designation'}
                  {employeeSummary.employee.is_active === false && (
                    <Badge variant="destructive" className="ml-2">Inactive</Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(employeeSummary.portals).map(([key, portal]) => (
                    <div 
                      key={key} 
                      className={`p-3 rounded-lg bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                        selectedPortal === key ? 'ring-2 ring-teal-500' : ''
                      }`}
                      onClick={() => {
                        setSelectedPortal(key);
                        setTimeout(fetchEntries, 100);
                      }}
                    >
                      <p className="text-xs text-gray-500 truncate">{portal.name}</p>
                      <p className="text-xl font-bold text-teal-600">{portal.count}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Entries Table */}
          {selectedEmployeeInfo && entries.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {portals.find(p => p.key === selectedPortal)?.name} - {selectedEmployeeInfo.name}
                    </CardTitle>
                    <CardDescription>
                      Showing all {entries.length} records (including historical data)
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-teal-600">
                    <Eye className="w-3 h-3 mr-1" /> Read-Only
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        {displayFields.map((field) => (
                          <TableHead key={field} className="capitalize">
                            {field.replace(/_/g, ' ')}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry, idx) => (
                        <TableRow key={entry.id || idx}>
                          <TableCell className="font-medium">{idx + 1}</TableCell>
                          {displayFields.map((field) => (
                            <TableCell key={field}>
                              {formatValue(entry[field], field)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedEmployeeInfo && entries.length === 0 && !loadingEntries && (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No records found for this employee in the selected portal.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Employee Detail Dialog - All Emp_Tab Fields */}
      <Dialog open={showEmployeeDetail} onOpenChange={setShowEmployeeDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="employee-detail-dialog">
          {viewingEmployee && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {viewingEmployee.name} ({viewingEmployee.employee_id})
                  {viewingEmployee.is_active === false && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* User Basic Info Header */}
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="font-medium">{viewingEmployee.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Role</p>
                      <p className="font-medium capitalize">{viewingEmployee.role}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Date of Joining</p>
                      <p className="font-medium">{formatValue(viewingEmployee.date_of_joining, 'date')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      {viewingEmployee.is_active !== false ? (
                        <Badge className="bg-green-500">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* All Field Groups */}
                {Object.entries(EMP_TAB_FIELD_GROUPS).map(([groupName, fields]) => {
                  const GroupIcon = getGroupIcon(groupName);
                  return (
                    <Card key={groupName}>
                      <CardHeader className="py-3 bg-gray-50">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <GroupIcon className="w-4 h-4 text-teal-600" />
                          {groupName}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {fields.map(({ key, label }) => {
                            const value = getFieldValue(viewingEmployee, key);
                            return (
                              <div key={key} className="space-y-1">
                                <p className="text-xs text-gray-500">{label}</p>
                                <p className="text-sm font-medium break-words">
                                  {formatValue(value, key)}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {/* Timestamps */}
                <Card>
                  <CardHeader className="py-3 bg-gray-50">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-teal-600" />
                      Record Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Created At</p>
                        <p className="text-sm font-medium">
                          {formatValue(viewingEmployee.emp_details?.created_at || viewingEmployee.created_at, 'date')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Last Updated</p>
                        <p className="text-sm font-medium">
                          {formatValue(viewingEmployee.emp_details?.updated_at, 'date')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">User ID</p>
                        <p className="text-sm font-medium text-gray-500 truncate">
                          {viewingEmployee.id}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRPortal;
